import { Box, Grid } from "@mui/material";
import { Loading, LoadingFailedPage } from "~/components/loading";
import { CopyTableToClipboardProps } from "~/components/copy-table-to-clipboard";
import { Space } from "~/lib/types";
import { AssetSoftwareScanningEmptyState } from "./AssetSoftwareScanningEmptyState";
import { useAssetSoftware } from "./hooks/useAssetSoftware";
import { AssetSoftwareTableEmptyState } from "~/pages/inventory/components/AssetSoftware/AssetSoftwareTableEmptyState";
import { AssetSoftwareData } from "~/pages/inventory/components/AssetSoftware/AssetSoftwareData";
import { AssetSoftwareFilterSection } from "~/pages/inventory/components/AssetSoftware/AssetSoftwareFilterSection/AssetSoftwareFilterSection";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";

type SoftwareProps = {
  assetMrn: string;
  clipboardDetails: CopyTableToClipboardProps["tableData"]["details"];
  scope: SpaceOrWorkspaceScope;
  space: Space;
  isCicd?: boolean;
  isVulnerabilityScanningEnabled: boolean;
};

export const AssetSoftware = ({
  assetMrn,
  clipboardDetails,
  scope,
  space,
  isVulnerabilityScanningEnabled,
}: SoftwareProps) => {
  const {
    isLoading,
    isLoadingFailed,
    isScanningEmptyState,
    packageScores,
    emptyStateVariant,
    showCopyToClipboard,
    tableData,
    hasData,
    sort,
    paginate,
    filterContext,
  } = useAssetSoftware({
    assetMrn,
    clipboardDetails,
    isVulnerabilityScanningEnabled,
  });

  if (isLoadingFailed) {
    return <LoadingFailedPage what="packages" />;
  }

  if (!isLoading && isScanningEmptyState) {
    return (
      <AssetSoftwareScanningEmptyState
        space={space}
        packageScores={packageScores}
        variant={emptyStateVariant}
      />
    );
  }

  return (
    <Box>
      <AssetSoftwareFilterSection
        filterContext={filterContext}
        showCopyToClipboard={showCopyToClipboard}
        tableData={tableData}
      />
      {isLoading ? (
        <Box my={10}>
          <Loading what="packages" />
        </Box>
      ) : hasData ? (
        <AssetSoftwareData
          scope={scope}
          packageScores={packageScores}
          sort={sort}
          assetMrn={assetMrn}
          paginationProps={paginate.pageProps(packageScores?.pageInfo)}
        />
      ) : (
        <Grid mt={3} size={12}>
          <AssetSoftwareTableEmptyState
            variant={emptyStateVariant}
            handleResetFilters={() => filterContext.resetFilters()}
          />
        </Grid>
      )}
    </Box>
  );
};
